import {
  OnboardingCollectedData,
  OnboardingStatus,
  OnboardingStep,
  OrgPersonalizationSettings,
  PersonalizationMode,
  UserPersonalizationSettings,
} from '@ctrl-enter/types';
import { create } from 'zustand';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:store:personalization');
import { devtools, persist } from 'zustand/middleware';

type PersonalizationStoreState = {
  // Which preset is active: 'org' (use org settings) or 'custom' (use user overrides)
  activeMode: PersonalizationMode;

  // User's custom settings (overrides/additions)
  userSettings: UserPersonalizationSettings;

  // Organization settings (fetched from WorkOS, read-only for non-admins)
  orgSettings: OrgPersonalizationSettings | null;

  // Onboarding state
  onboardingStatus: OnboardingStatus;
  onboardingData: OnboardingCollectedData;
  showOnboarding: boolean;

  // Actions
  setActiveMode: (mode: PersonalizationMode) => void;
  setUserSettings: (settings: UserPersonalizationSettings) => void;
  setOrgSettings: (settings: OrgPersonalizationSettings | null) => void;
  updateUserSettings: (partial: Partial<UserPersonalizationSettings>) => void;
  clearUserSettings: () => void;
  clearAllData: () => void;

  // Onboarding actions
  setOnboardingStatus: (status: OnboardingStatus) => void;
  setOnboardingStep: (step: OnboardingStep) => void;
  updateOnboardingData: (data: Partial<OnboardingCollectedData>) => void;
  completeOnboarding: () => void;
  skipOnboarding: () => void;
  restartOnboarding: () => void;
  setShowOnboarding: (show: boolean) => void;
};

/**
 * Personalization store
 * Manages user-level and org-level personalization settings
 * Follows the hybrid preset pattern: users can choose to use org defaults or customize
 */
export const usePersonalizationStore = create<PersonalizationStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        // Default state
        activeMode: 'org', // Default to org settings
        userSettings: {},
        orgSettings: null,

        // Onboarding default state
        onboardingStatus: {
          completed: false,
          skipped: false,
        },
        onboardingData: {},
        showOnboarding: false,

        // Set which mode is active
        setActiveMode: mode => {
          logger.info('🎯 [PERSONALIZATION] Setting active mode:', mode);
          set({ activeMode: mode });
        },

        // Set user settings (full replacement)
        setUserSettings: settings => {
          logger.info(
            '👤 [PERSONALIZATION] Setting user settings:',
            JSON.stringify(settings, null, 2)
          );
          set({ userSettings: settings });
        },

        // Set org settings (fetched from backend)
        setOrgSettings: settings => {
          logger.info(
            '🏢 [PERSONALIZATION] Setting org settings:',
            JSON.stringify(settings, null, 2)
          );
          set({ orgSettings: settings });
        },

        // Update user settings (partial update)
        updateUserSettings: partial => {
          const current = get().userSettings;
          const updated = { ...current, ...partial };
          logger.info('👤 [PERSONALIZATION] Updating user settings:', {
            current,
            partial,
            updated,
          });
          set({ userSettings: updated });
        },

        // Clear user settings (revert to empty)
        clearUserSettings: () => {
          logger.info('👤 [PERSONALIZATION] Clearing user settings');
          set({ userSettings: {} });
        },

        // Clear all personalization data (called on logout)
        clearAllData: () => {
          logger.info('🧹 [PERSONALIZATION] Clearing all personalization data on logout');
          set({
            activeMode: 'org', // Reset to default
            userSettings: {},
            orgSettings: null,
            onboardingStatus: {
              completed: false,
              skipped: false,
            },
            onboardingData: {},
            showOnboarding: false,
          });
        },

        // Onboarding actions
        setOnboardingStatus: status => {
          logger.info('🎬 [ONBOARDING] Setting onboarding status:', status);
          set({ onboardingStatus: status });
        },

        setOnboardingStep: step => {
          logger.info('📍 [ONBOARDING] Setting onboarding step:', step);
          set(state => ({
            onboardingStatus: { ...state.onboardingStatus, currentStep: step },
          }));
        },

        updateOnboardingData: data => {
          logger.info('📝 [ONBOARDING] Updating onboarding data:', data);
          set(state => ({
            onboardingData: { ...state.onboardingData, ...data },
          }));
        },

        completeOnboarding: () => {
          logger.info('✅ [ONBOARDING] Completing onboarding');
          logger.info('✅ [ONBOARDING] Setting showOnboarding to FALSE');
          set({
            onboardingStatus: {
              completed: true,
              completedAt: new Date().toISOString(),
              skipped: false,
              currentStep: 'complete',
            },
            showOnboarding: false,
            onboardingData: {}, // Clear collected data
          });
          logger.info('✅ [ONBOARDING] Onboarding state updated');
        },

        skipOnboarding: () => {
          logger.info('⏭️ [ONBOARDING] Skipping onboarding');
          set({
            onboardingStatus: {
              completed: false,
              skipped: true,
              skippedAt: new Date().toISOString(),
            },
            showOnboarding: false,
            onboardingData: {}, // Clear collected data
          });
        },

        restartOnboarding: () => {
          logger.info('🔄 [ONBOARDING] Restarting onboarding');
          set({
            onboardingStatus: {
              completed: false,
              skipped: false,
              currentStep: 'welcome',
            },
            onboardingData: {},
            showOnboarding: true,
          });
        },

        setShowOnboarding: show => {
          logger.info(`🎬 [PERSONALIZATION-STORE] setShowOnboarding called with:`, show);
          console.trace('Stack trace:');
          set({ showOnboarding: show });
        },
      }),
      {
        name: 'personalization-store',
        partialize: state => ({
          activeMode: state.activeMode,
          userSettings: state.userSettings,
          onboardingStatus: state.onboardingStatus,
          // Don't persist orgSettings - always fetch fresh from WorkOS
          // Don't persist onboardingData - transient collection state
          // Don't persist showOnboarding - computed at runtime
        }),
      }
    ),
    { name: 'personalization-store' }
  )
);

// Atomic selectors (following repo pattern)
export const useActiveMode = () => usePersonalizationStore(state => state.activeMode);
export const useUserSettings = () => usePersonalizationStore(state => state.userSettings);
export const useOrgSettings = () => usePersonalizationStore(state => state.orgSettings);
export const useSetActiveMode = () => usePersonalizationStore(state => state.setActiveMode);
export const useSetUserSettings = () => usePersonalizationStore(state => state.setUserSettings);
export const useSetOrgSettings = () => usePersonalizationStore(state => state.setOrgSettings);
export const useUpdateUserSettings = () =>
  usePersonalizationStore(state => state.updateUserSettings);
export const useClearUserSettings = () => usePersonalizationStore(state => state.clearUserSettings);

// Onboarding selectors
export const useOnboardingStatus = () => usePersonalizationStore(state => state.onboardingStatus);
export const useOnboardingData = () => usePersonalizationStore(state => state.onboardingData);
export const useShowOnboarding = () => usePersonalizationStore(state => state.showOnboarding);
export const useSetOnboardingStatus = () =>
  usePersonalizationStore(state => state.setOnboardingStatus);
export const useSetOnboardingStep = () => usePersonalizationStore(state => state.setOnboardingStep);
export const useUpdateOnboardingData = () =>
  usePersonalizationStore(state => state.updateOnboardingData);
export const useCompleteOnboarding = () =>
  usePersonalizationStore(state => state.completeOnboarding);
export const useSkipOnboarding = () => usePersonalizationStore(state => state.skipOnboarding);
export const useRestartOnboarding = () => usePersonalizationStore(state => state.restartOnboarding);
export const useSetShowOnboarding = () => usePersonalizationStore(state => state.setShowOnboarding);
